import React, { useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(5, 3, 15, 0.78);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Dialog = styled.div<{ $wide?: boolean }>`
  width: min(${({ $wide }) => ($wide ? '920px' : '560px')}, 100%);
  max-height: min(90vh, 900px);
  display: flex;
  flex-direction: column;
  background: linear-gradient(160deg, rgba(32, 22, 58, 0.98), rgba(18, 12, 36, 0.98));
  border: 1px solid rgba(255, 20, 147, 0.35);
  border-radius: 16px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.55), 0 0 40px rgba(255, 20, 147, 0.12);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 20, 147, 0.18);
  background: rgba(255, 20, 147, 0.06);
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.heading};
  font-size: 1.05rem;
  color: ${theme.colors.pink};
  letter-spacing: 0.5px;
`;

const CloseBtn = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: transparent;
  color: ${theme.colors.textMuted};
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  &:hover { color: #fff; border-color: ${theme.colors.pink}; }
`;

const Body = styled.div`
  padding: 1.15rem 1.25rem;
  overflow-y: auto;
  flex: 1;
`;

const Footer = styled.div`
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
  flex-wrap: wrap;
  padding: 0.9rem 1.25rem;
  border-top: 1px solid rgba(255, 20, 147, 0.15);
  background: rgba(0, 0, 0, 0.15);
`;

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}

const Modal: React.FC<ModalProps> = ({ open, title, onClose, children, footer, wide }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Overlay onClick={onClose} role="presentation">
      <Dialog $wide={wide} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <Header>
          <Title id="modal-title">{title}</Title>
          <CloseBtn type="button" onClick={onClose} aria-label="Cerrar">×</CloseBtn>
        </Header>
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Dialog>
    </Overlay>
  );
};

export default Modal;
